import { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '../utils/cn'

// Sample cart items
const initialCartItems = [
  {
    id: 1,
    name: 'Rose Quartz',
    category: 'NAKHOONS',
    price: 49,
    image: '/images/product-1.png',
    color: 'Rose Pink',
    size: 'M',
    quantity: 1,
  },
  {
    id: 4,
    name: 'Golden Hour',
    category: 'NAKHOONS',
    price: 59,
    image: '/images/product-1.png',
    color: 'Gold',
    size: 'S',
    quantity: 2,
  },
]

function CartItem({ item, onUpdateQuantity, onRemove }) {
  const [imageLoaded, setImageLoaded] = useState(false)

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -100 }}
      className="flex gap-6 py-6 border-b border-border"
    >
      {/* Product Image */}
      <Link to={`/product/${item.id}`} className="flex-shrink-0">
        <div className="w-24 h-24 lg:w-32 lg:h-32 bg-bg-tertiary overflow-hidden">
          <img
            src={item.image}
            alt={item.name}
            className={cn(
              "w-full h-full object-cover transition-all duration-500",
              imageLoaded ? "opacity-100" : "opacity-0"
            )}
            onLoad={() => setImageLoaded(true)}
          />
        </div>
      </Link>

      {/* Product Details */}
      <div className="flex-1 flex flex-col justify-between">
        <div>
          <Link to={`/product/${item.id}`}>
            <p className="text-xs uppercase tracking-[0.15em] text-text-muted mb-1">
              {item.category}
            </p>
            <h3 className="text-sm lg:text-base font-medium text-text-primary uppercase tracking-wider hover:text-text-secondary transition-colors">
              {item.name}
            </h3>
          </Link>
          <p className="text-xs text-text-muted mt-2">
            {item.color} / Size {item.size}
          </p>
        </div>

        <div className="flex items-center justify-between mt-4">
          {/* Quantity Selector */}
          <div className="flex items-center border border-border">
            <button
              onClick={() => onUpdateQuantity(item.id, Math.max(1, item.quantity - 1))}
              className="w-8 h-8 flex items-center justify-center text-text-secondary hover:text-text-primary transition-colors"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 12H4" />
              </svg>
            </button>
            <span className="w-10 h-8 flex items-center justify-center text-text-primary text-sm">
              {item.quantity}
            </span>
            <button
              onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
              className="w-8 h-8 flex items-center justify-center text-text-secondary hover:text-text-primary transition-colors"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>

          {/* Remove Button */}
          <button
            onClick={() => onRemove(item.id)}
            className="text-xs uppercase tracking-wider text-text-muted hover:text-text-primary transition-colors underline"
          >
            Remove
          </button>
        </div>
      </div>

      {/* Price */}
      <div className="text-right">
        <p className="text-base lg:text-lg font-display text-text-primary">
          ${item.price * item.quantity}
        </p>
        {item.quantity > 1 && (
          <p className="text-xs text-text-muted mt-1">
            ${item.price} each
          </p>
        )}
      </div>
    </motion.div>
  )
}

function EmptyCart() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center py-20"
    >
      <div className="w-24 h-24 mx-auto mb-6 bg-bg-tertiary rounded-full flex items-center justify-center">
        <svg className="w-10 h-10 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
        </svg>
      </div>
      <h2 className="text-2xl font-serif text-text-primary mb-4">Your Cart is Empty</h2>
      <p className="text-text-secondary mb-8 max-w-md mx-auto">
        Looks like you haven't added anything to your cart yet. Explore our collections and find your perfect match.
      </p>
      <Link
        to="/"
        className={cn(
          "inline-flex items-center justify-center",
          "px-8 py-4",
          "bg-text-primary text-white",
          "text-sm font-medium uppercase tracking-wider",
          "hover:bg-primary-light transition-colors duration-300"
        )}
      >
        Continue Shopping
      </Link>
    </motion.div>
  )
}

export default function CartPage() {
  const [cartItems, setCartItems] = useState(initialCartItems)

  const updateQuantity = (id, newQuantity) => {
    setCartItems(items =>
      items.map(item =>
        item.id === id ? { ...item, quantity: newQuantity } : item
      )
    )
  }

  const removeItem = (id) => {
    setCartItems(items => items.filter(item => item.id !== id))
  }

  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0)
  const shipping = subtotal > 100 ? 0 : 8
  const total = subtotal + shipping

  return (
    <div className="min-h-screen bg-bg-primary pt-20 lg:pt-24">
      {/* Page Header */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 lg:py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <h1 className="text-3xl lg:text-4xl font-serif text-text-primary mb-2">Shopping Cart</h1>
          <p className="text-sm text-text-muted uppercase tracking-wider">
            {cartItems.length} {cartItems.length === 1 ? 'item' : 'items'}
          </p>
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 pb-20 lg:pb-32">
        {cartItems.length === 0 ? (
          <EmptyCart />
        ) : (
          <div className="grid lg:grid-cols-3 gap-12 lg:gap-16">
            {/* Cart Items */}
            <div className="lg:col-span-2">
              {/* Table Header - Desktop */}
              <div className="hidden lg:grid lg:grid-cols-12 gap-4 py-4 border-b border-border text-xs uppercase tracking-wider text-text-muted">
                <div className="col-span-6">Product</div>
                <div className="col-span-3">Quantity</div>
                <div className="col-span-3 text-right">Total</div>
              </div>

              {/* Cart Items List */}
              <AnimatePresence mode="popLayout">
                {cartItems.map((item) => (
                  <CartItem
                    key={item.id}
                    item={item}
                    onUpdateQuantity={updateQuantity}
                    onRemove={removeItem}
                  />
                ))}
              </AnimatePresence>

              {/* Continue Shopping */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="mt-8"
              >
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-sm uppercase tracking-wider text-text-secondary hover:text-text-primary transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Continue Shopping
                </Link>
              </motion.div>
            </div>

            {/* Order Summary */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-1"
            >
              <div className="bg-bg-secondary p-6 lg:p-8 sticky top-28">
                <h2 className="text-xs uppercase tracking-[0.2em] text-text-primary mb-6">
                  Order Summary
                </h2>

                {/* Summary Lines */}
                <div className="space-y-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Subtotal</span>
                    <span className="text-text-primary">${subtotal}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Shipping</span>
                    <span className="text-text-primary">
                      {shipping === 0 ? 'Free' : `$${shipping}`}
                    </span>
                  </div>
                  {subtotal < 100 && (
                    <p className="text-xs text-text-muted">
                      Add ${100 - subtotal} more for free shipping
                    </p>
                  )}
                </div>

                {/* Divider */}
                <div className="w-full h-px bg-border my-6" />

                {/* Total */}
                <div className="flex justify-between items-center mb-8">
                  <span className="text-sm uppercase tracking-wider text-text-primary">Total</span>
                  <span className="text-xl font-display text-text-primary">${total}</span>
                </div>

                {/* Checkout Button */}
                <Link
                  to="/checkout"
                  className={cn(
                    "w-full flex items-center justify-center",
                    "h-12 px-8",
                    "bg-text-primary text-white",
                    "text-sm font-medium uppercase tracking-wider",
                    "hover:bg-primary-light transition-colors duration-300"
                  )}
                >
                  Checkout
                </Link>

                {/* Promo Code */}
                <div className="mt-6">
                  <button className="w-full text-left text-xs uppercase tracking-wider text-text-muted hover:text-text-primary transition-colors flex items-center justify-between">
                    <span>Have a promo code?</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>

                {/* Trust Badges */}
                <div className="mt-8 pt-6 border-t border-border">
                  <div className="flex items-center gap-4 text-text-muted">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      <span className="text-xs">Secure</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                      <span className="text-xs">Encrypted</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  )
}
